import type {Action} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {ConfiguratorActionBar} from '@dms/features/new-vehicle';

import {t} from './catalogueUi';

type WizardFooterProps = {
  step: number;
  issue?: string;
  isContinueDisabled: boolean;
  isCreateDisabled: boolean;
  isCreating: boolean;
  storageFailed: boolean;
  hint?: string;
  onBack: () => void;
  onContinue: () => void;
  onCreate: () => void;
  /** Set once the catalogue draft exists; it then replaces the trailing action. */
  onInspect?: () => void;
};

export function WizardFooter(props: WizardFooterProps) {
  const hint = props.issue ?? props.hint;
  const leadingActions: Action[] =
    props.step > 1
      ? [
          {
            type: 'button',
            variant: 'secondary',
            title: t('general.actions.back', 'Back'),
            'aria-label': t('general.actions.back', 'Back'),
            leftIcon: 'navigation/arrow_back',
            onClick: props.onBack,
          },
        ]
      : [];
  // Once the draft exists there is nothing left to create or continue to, so the only action
  // worth offering is the one that opens it.
  const trailingActions: Action[] = props.onInspect // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? [
        {
          type: 'button',
          variant: 'primary',
          title: t(
            'entity.newVehicle.authoring.openCreatedDraft',
            'Inspect created model-year draft'
          ),
          'aria-label': t(
            'entity.newVehicle.authoring.openCreatedDraft',
            'Inspect created model-year draft'
          ),
          leftIcon: 'action/open_in_new',
          onClick: props.onInspect,
        },
      ]
    : props.step < 10
      ? [
          {
            type: 'button',
            variant: 'primary',
            title: t('general.actions.continue', 'Continue'),
            'aria-label': t('general.actions.continue', 'Continue'),
            rightIcon: 'navigation/arrow_forward',
            isDisabled: props.isContinueDisabled,
            onClick: props.onContinue,
          },
        ]
      : [
          {
            type: 'button',
            variant: 'primary',
            title: t(
              'entity.newVehicle.authoring.createDraftInCatalogue',
              'Create draft in catalogue'
            ),
            isDisabled: props.isCreateDisabled,
            isLoading: props.isCreating,
            onClick: props.onCreate,
          },
        ];

  return (
    <ConfiguratorActionBar
      leading={
        <VStack spacing={0}>
          <Text size="xxSmall" color="secondary" alternative>
            {t(
              props.storageFailed
                ? 'entity.newVehicle.authoring.draftNotSavedCaption'
                : 'entity.newVehicle.authoring.draftSavedCaption',
              props.storageFailed ? 'Not saved in this browser' : 'Saved in this browser'
            ).toUpperCase()}
          </Text>
          <Text size="small" alternative>
            {t('entity.newVehicle.authoring.stepProgress', 'Step {step} of 10', {
              step: props.step,
            })}
          </Text>
        </VStack>
      }
      hint={hint}
      hintTone={props.issue || props.storageFailed ? 'warning' : 'neutral'}
      leadingActions={leadingActions}
      trailingActions={trailingActions}
      data-testid="new-model-wizard"
    />
  );
}
